// Cave84 — Market Prices service (Phase 4 Week 13)
// Spec §4.7 F7. Reads / writes for vintages/{vintageId}/market_price_history.
// Also provides helpers for queueing market_price_refresh jobs.

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { MarketPriceHistory, Vintage } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';

// ---------------------------------------------------------------------------
// Collection helpers
// ---------------------------------------------------------------------------

function priceCol(vintageId: string) {
  return collection(db, 'vintages', vintageId, 'market_price_history');
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Fetch the full price history for a vintage, newest first. */
export async function listPriceHistory(vintageId: string): Promise<MarketPriceHistory[]> {
  const snap = await getDocs(
    query(priceCol(vintageId), orderBy('date_observed', 'desc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MarketPriceHistory, 'id'>) }));
}

/** Fetch only the most recent price entry for a vintage. */
export async function getLatestPrice(vintageId: string): Promise<MarketPriceHistory | null> {
  const snap = await getDocs(
    query(priceCol(vintageId), orderBy('date_observed', 'desc'), limit(1))
  );
  if (snap.empty) return null;
  const d = snap.docs[0]!;
  return { id: d.id, ...(d.data() as Omit<MarketPriceHistory, 'id'>) };
}

/** Fetch the latest price for many vintages in parallel (used by Investment dashboard). */
export async function getLatestPricesForVintages(
  vintageIds: string[]
): Promise<Map<string, MarketPriceHistory>> {
  const results = await Promise.all(
    vintageIds.map(async (id) => {
      const price = await getLatestPrice(id);
      return [id, price] as [string, MarketPriceHistory | null];
    })
  );
  const map = new Map<string, MarketPriceHistory>();
  for (const [id, price] of results) {
    if (price) map.set(id, price);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Writes — Owner actions
// ---------------------------------------------------------------------------

/**
 * Manually record a price entry for a vintage (Owner override or manual entry).
 * Appends to the price history — never replaces existing entries.
 */
export async function recordManualPrice(
  vintageId: string,
  price: number,
  currency: MarketPriceHistory['currency'] = 'EUR'
): Promise<string> {
  const now = serverTimestamp();
  const ref = await addDoc(priceCol(vintageId), {
    vintage_id: vintageId,
    format: '750ml',
    currency,
    price,
    date_observed: now,
    source: 'manual',
    confidence: 'high',
  });
  // Stamp parent vintage so wizard date-filter knows this was recently priced
  await updateDoc(doc(db, 'vintages', vintageId), { price_last_refreshed_at: now });
  return ref.id;
}

// ---------------------------------------------------------------------------
// Queue: bulk enqueue market_price_refresh jobs
// ---------------------------------------------------------------------------

export type PriceRefreshScope =
  | { type: 'all' }
  | { type: 'region'; region: string }
  | { type: 'producer'; producerId: string }
  | { type: 'vintage_ids'; ids: string[] };

/**
 * Enqueue market_price_refresh jobs for vintages matching the given scope.
 * Skips vintages already queued as pending/processing.
 * Returns the number of new entries created.
 *
 * priority='high' → picked up instantly by the autoRefreshNewVintagePrice
 * Cloud Function trigger (apps/functions/src/index.ts), no CLI run needed.
 * priority='normal' → NOT auto-processed (cost-discipline guardrail against
 * a web_search "token bomb" on large batches); requires the CLI script or
 * a future throttled trigger. Keep bulk calls small when using 'high'.
 */
export async function enqueuePriceRefresh(
  vintages: Vintage[],
  scope: PriceRefreshScope,
  wineRegionMap: Map<string, string>,   // wineId → region string
  wineProducerMap: Map<string, string>, // wineId → producerId
  priority: 'high' | 'normal' = 'normal'
): Promise<number> {
  // Determine target vintage IDs from scope
  let targetIds: string[];
  if (scope.type === 'all') {
    targetIds = vintages.map((v) => v.id);
  } else if (scope.type === 'vintage_ids') {
    targetIds = scope.ids;
  } else if (scope.type === 'region') {
    targetIds = vintages
      .filter((v) => (wineRegionMap.get(v.wine_id) ?? '').toLowerCase().includes(scope.region.toLowerCase()))
      .map((v) => v.id);
  } else {
    targetIds = vintages
      .filter((v) => wineProducerMap.get(v.wine_id) === scope.producerId)
      .map((v) => v.id);
  }

  if (targetIds.length === 0) return 0;

  // Fetch already-pending/processing entries to avoid duplication
  const pendingSnap = await getDocs(
    query(
      typedCollection<{ id: string; entity_id: string; entity_type: string; status: string }>
        ('generation_queue'),
      where('entity_type', '==', 'market_price_refresh'),
      where('status', 'in', ['pending', 'processing'])
    )
  );
  const alreadyQueued = new Set(pendingSnap.docs.map((d) => d.data().entity_id as string));

  let count = 0;
  for (const vintageId of targetIds) {
    if (alreadyQueued.has(vintageId)) continue;
    const ref = doc(collection(db, 'generation_queue'));
    await setDoc(ref, {
      entity_type: 'market_price_refresh',
      entity_id: vintageId,
      priority,
      status: 'pending',
      requested_at: serverTimestamp(),
      trigger_reason: 'owner_manual_refresh',
    });
    count++;
  }
  return count;
}

/**
 * Force-refresh: clears any stuck pending/processing generation_queue
 * entries for the given vintages, then writes brand-new priority='high'
 * entries for all of them.
 *
 * enqueuePriceRefresh() is dedup-safe by design (skips a vintage that
 * already has a pending/processing entry) — that's correct for the normal
 * case, but means a genuinely stuck entry (e.g. the Cloud Function's
 * Eventarc trigger silently dropped, or the run crashed before updating
 * status) sits forever and blocks re-queueing. This is the owner-facing
 * self-heal path for that: it always creates a fresh document, and a
 * fresh document always fires onDocumentCreated again.
 *
 * Returns the number of fresh entries created (== vintageIds.length).
 */
export async function forceRequeuePriceRefresh(vintageIds: string[]): Promise<number> {
  if (vintageIds.length === 0) return 0;

  // Firestore 'in' queries cap at 30 values — chunk defensively.
  const CHUNK = 10;
  const staleDocs: { ref: ReturnType<typeof doc> }[] = [];
  for (let i = 0; i < vintageIds.length; i += CHUNK) {
    const chunk = vintageIds.slice(i, i + CHUNK);
    const snap = await getDocs(
      query(
        typedCollection<{ id: string; entity_id: string; entity_type: string; status: string }>
          ('generation_queue'),
        where('entity_type', '==', 'market_price_refresh'),
        where('entity_id', 'in', chunk),
        where('status', 'in', ['pending', 'processing'])
      )
    );
    for (const d of snap.docs) staleDocs.push({ ref: d.ref });
  }

  const clearedAt = serverTimestamp();
  await Promise.all(
    staleDocs.map(({ ref }) =>
      updateDoc(ref, {
        status: 'failed',
        completed_at: clearedAt,
        result_data: { error: 'stuck_force_retry_by_owner' },
      })
    )
  );

  let count = 0;
  for (const vintageId of vintageIds) {
    const ref = doc(collection(db, 'generation_queue'));
    await setDoc(ref, {
      entity_type: 'market_price_refresh',
      entity_id: vintageId,
      priority: 'high',
      status: 'pending',
      requested_at: serverTimestamp(),
      trigger_reason: 'owner_force_retry',
    });
    count++;
  }
  return count;
}

// ---------------------------------------------------------------------------
// Review: resolve needs_owner_review items
// ---------------------------------------------------------------------------

export type ReviewDecision =
  | { action: 'keep_current' }
  | { action: 'use_suggested'; price: number; currency?: MarketPriceHistory['currency'] }
  | { action: 'manual_entry'; price: number; currency?: MarketPriceHistory['currency'] };

/**
 * Resolve a needs_owner_review queue entry.
 *   keep_current → mark as completed, no price written
 *   use_suggested / manual_entry → write price + mark completed
 */
export async function resolveReviewEntry(
  queueEntryId: string,
  vintageId: string,
  decision: ReviewDecision
): Promise<void> {
  const entryRef = doc(db, 'generation_queue', queueEntryId);

  if (decision.action === 'keep_current') {
    await updateDoc(entryRef, {
      status: 'completed',
      completed_at: serverTimestamp(),
      result_data: { resolution: 'kept_current' },
    });
    return;
  }

  // Write new price entry
  const resolveNow = serverTimestamp();
  await addDoc(priceCol(vintageId), {
    vintage_id: vintageId,
    format: '750ml',
    currency: decision.currency ?? 'EUR',
    price: decision.price,
    date_observed: resolveNow,
    source: 'manual',
    confidence: 'high',
  });
  // Stamp parent vintage
  await updateDoc(doc(db, 'vintages', vintageId), { price_last_refreshed_at: resolveNow });

  await updateDoc(entryRef, {
    status: 'completed',
    completed_at: serverTimestamp(),
    result_data: {
      resolution: decision.action,
      price_written: decision.price,
      currency: decision.currency ?? 'EUR',
    },
  });
}
